import {
  MClassLavTargetValuesEnum,
  MClassUoTargetValuesEnum,
  MClassUlTargetValuesEnum,
  MClassFTiTargetValuesEnum,
  MClassReiTargetValuesEnum,
  MCustomClassData,
} from "types";
import { CapturedHeatmaps, CarboneLightingClassConfig, MetStatus } from "./types";
import { roundToTwo, normalizeStatus, checkMetStatus, getStatusText } from "./status";

export function getMClassTargets(roadClass: string, customRoadClass?: MCustomClassData) {
  if (customRoadClass?.class_type) {
    return {
      lav: String(customRoadClass.Lm_av || ""),
      u0: String(customRoadClass.L_uo || ""),
      ul: String(customRoadClass.L_ul || ""),
      fti: String(customRoadClass.fTI || ""),
      rei: String(customRoadClass.Rei || ""),
    };
  }
  return {
    lav: MClassLavTargetValuesEnum[roadClass as keyof typeof MClassLavTargetValuesEnum] || "",
    u0: MClassUoTargetValuesEnum[roadClass as keyof typeof MClassUoTargetValuesEnum] || "",
    ul: MClassUlTargetValuesEnum[roadClass as keyof typeof MClassUlTargetValuesEnum] || "",
    fti: MClassFTiTargetValuesEnum[roadClass as keyof typeof MClassFTiTargetValuesEnum] || "",
    rei: MClassReiTargetValuesEnum[roadClass as keyof typeof MClassReiTargetValuesEnum] || "",
  };
}

type BuildLuminanceTablesParams = {
  luminance: unknown;
  heatmaps?: CapturedHeatmaps;
  roadClass: string;
  customRoadClass?: MCustomClassData;
  calcFieldDimX: number;
  calcFieldDimY: number;
  calcFieldPtsX: number;
  calcFieldPtsY: number;
  ulor?: string;
  t: (key: string) => string;
};

export function buildLuminanceTables({
  luminance,
  heatmaps,
  roadClass,
  customRoadClass,
  calcFieldDimX,
  calcFieldDimY,
  calcFieldPtsX,
  calcFieldPtsY,
  ulor,
  t,
}: BuildLuminanceTablesParams) {
  if (!luminance) return [];

  const ulorValue = ulor ? Math.round(Number(ulor)) : 0;
  const targets = getMClassTargets(roadClass, customRoadClass);

  return Object.keys(luminance as Record<string, any>).map((key, index) => {
    const laneData = (luminance as Record<string, any>)[key];

    const laneDimX = laneData?.["dim.x"] ? roundToTwo(Number.parseFloat(laneData["dim.x"])) : calcFieldDimX;
    const laneDimY = laneData?.["dim.y"] ? roundToTwo(Number.parseFloat(laneData["dim.y"])) : calcFieldDimY;
    const lanePtsX = laneData?.["pts.x"] ? Number.parseInt(laneData["pts.x"]) : calcFieldPtsX;
    const lanePtsY = laneData?.["pts.y"] ? Number.parseInt(laneData["pts.y"]) : calcFieldPtsY;
    const yPosition = laneData?.["obs.y"] ? String(roundToTwo(Number.parseFloat(laneData["obs.y"]))) : "";

    const resultLav = roundToTwo(laneData?.l_avg || 0);
    const resultU0 = roundToTwo(laneData?.u_o || 0);
    const resultUl = roundToTwo(laneData?.u_l || 0);
    const resultFti = roundToTwo(laneData?.f_ti || 0);
    const resultRei = laneData?.rei === -1 ? "-" : String(roundToTwo(laneData?.rei || 0));

    const lavStatus = checkMetStatus(resultLav, Number.parseFloat(targets.lav) || 0, "gte");
    const u0Status = checkMetStatus(resultU0, Number.parseFloat(targets.u0) || 0, "gte");
    const ulStatus = checkMetStatus(resultUl, Number.parseFloat(targets.ul) || 0, "gte");
    const ftiTarget = Number.parseFloat(targets.fti.replace("%", "")) || 0;
    const ftiStatus = checkMetStatus(resultFti, ftiTarget, "lte");
    const reiStatus = resultRei === "-" ? ("met" as MetStatus) : checkMetStatus(Number.parseFloat(resultRei), Number.parseFloat(targets.rei) || 0, "gte");

    return {
      lane_index: index,
      lane_label: `${t("Lane")} ${Number.parseInt(key) + 1}`,
      class_name: customRoadClass?.class_type || roadClass,
      calculation_field: {
        dim_x: laneDimX,
        dim_y: laneDimY,
        pts_x: lanePtsX,
        pts_y: lanePtsY,
        formatted: `${laneDimX} m x ${laneDimY} m (${lanePtsX} x ${lanePtsY} ${t("pts")})`,
      },
      y_position: yPosition,
      target_l_av: targets.lav,
      result_l_av: String(resultLav),
      l_av_status: lavStatus,
      l_av_status_text: getStatusText(lavStatus, t),
      target_u0: targets.u0,
      result_u0: String(resultU0),
      u0_status: u0Status,
      u0_status_text: getStatusText(u0Status, t),
      target_ul: targets.ul,
      result_ul: String(resultUl),
      ul_status: ulStatus,
      ul_status_text: getStatusText(ulStatus, t),
      target_fti: targets.fti ? targets.fti.replace("%", " %") : "",
      result_fti: laneData?.f_ti != null ? `${resultFti} %` : "",
      fti_status: ftiStatus,
      fti_status_text: getStatusText(ftiStatus, t),
      target_rei: targets.rei,
      result_rei: resultRei,
      rei_status: reiStatus,
      rei_status_text: getStatusText(reiStatus, t),
      target_r_ul: ulor ? `${ulorValue} %` : "",
      result_r_ul: ulor ? `${ulorValue} %` : "",
      heatmap_base64: heatmaps?.luminance?.[key] || undefined,
    };
  });
}

type BuildLuminanceSummaryParams = {
  luminanceSummaryData?: Record<string, unknown>;
  firstLaneData?: Record<string, unknown>;
  summaryDimX: number;
  summaryDimY: number;
  summaryPtsX: number;
  summaryPtsY: number;
  roadClass: string;
  customRoadClass?: MCustomClassData;
  lightingClassConfig?: CarboneLightingClassConfig;
  ulor?: string;
  ulorValue: number;
  s_state?: string;
  e_state_left?: string;
  e_state_right?: string;
  hasBorderAreaLeft: boolean;
  hasBorderAreaRight: boolean;
  t: (key: string) => string;
};

export function buildLuminanceSummary({
  luminanceSummaryData,
  firstLaneData,
  summaryDimX,
  summaryDimY,
  summaryPtsX,
  summaryPtsY,
  roadClass,
  customRoadClass,
  lightingClassConfig,
  ulor,
  ulorValue,
  s_state,
  e_state_left,
  e_state_right,
  hasBorderAreaLeft,
  hasBorderAreaRight,
  t,
}: BuildLuminanceSummaryParams) {
  const summaryLav = luminanceSummaryData?.l_avg ? roundToTwo(Number(luminanceSummaryData.l_avg)) : 0;
  const summaryU0 = luminanceSummaryData?.u_o ? roundToTwo(Number(luminanceSummaryData.u_o)) : 0;
  const summaryUl = luminanceSummaryData?.u_l ? roundToTwo(Number(luminanceSummaryData.u_l)) : 0;
  const summaryFti = luminanceSummaryData?.f_ti ? roundToTwo(Number(luminanceSummaryData.f_ti)) : 0;
  const summaryRei = firstLaneData?.rei === -1 ? "-" : String(roundToTwo(Number(firstLaneData?.rei || 0)));

  const targets = getMClassTargets(roadClass, customRoadClass);

  return {
    calculation_field: {
      dim_x: summaryDimX,
      dim_y: summaryDimY,
      pts_x: summaryPtsX,
      pts_y: summaryPtsY,
      formatted: `${summaryDimX} m x ${summaryDimY} m (${summaryPtsX} x ${summaryPtsY} ${t("pts")})`,
    },
    class_name: customRoadClass?.class_type || roadClass,
    result_l_av: String(summaryLav),
    result_u0: String(summaryU0),
    result_ul: String(summaryUl),
    result_fti: luminanceSummaryData?.f_ti != null ? `${summaryFti} %` : "",
    result_rei: summaryRei,
    result_r_ul: ulor ? `${ulorValue} %` : "",
    target_l_av: targets.lav,
    target_u0: targets.u0,
    target_ul: targets.ul,
    target_fti: targets.fti ? targets.fti.replace("%", " %") : "",
    target_rei: targets.rei,
    target_r_ul: ulor ? `${ulorValue} %` : "",
    road_status: normalizeStatus(s_state),
    road_status_text: getStatusText(normalizeStatus(s_state), t),
    road_class: customRoadClass?.class_type || roadClass,
    border_left_status: normalizeStatus(e_state_left),
    border_left_status_text: getStatusText(normalizeStatus(e_state_left), t),
    border_left_class: lightingClassConfig?.borderAreaLeftClasses || "",
    border_right_status: normalizeStatus(e_state_right),
    border_right_status_text: getStatusText(normalizeStatus(e_state_right), t),
    border_right_class: lightingClassConfig?.borderAreaRightClasses || "",
    has_border_left: hasBorderAreaLeft,
    has_border_right: hasBorderAreaRight,
  };
}
